import { useMutation, useQueryClient } from '@tanstack/react-query';
import z from 'zod';
import { invalidateOrders, invalidateOrdersV3 } from '@hungryroot/orders';
import {
  StatusCode,
  patchApiV2CustomersCustomerId,
  type DayOfWeekNumbers,
  type Frequency,
} from '@hungryroot/rest';
import { invalidateCustomerData } from '@hungryroot/rest-hooks';
import { invalidateAccountSettings } from './useGetAccountSettings';

export const DeliveryScheduleSchema = z.object({
  day_of_week: z
    .enum(['0', '1', '2', '3', '4', '5', '6'])
    .transform((n) => parseInt(n) as DayOfWeekNumbers)
    .optional(),
  frequency: z
    .enum(['7', '14', '28'])
    .transform((n) => parseInt(n) as Frequency)
    .optional(),
});

// Avoiding z.infer here because it output these values as `number`
// instead of `DayOfWeekNumbers` or `Frequency`
export type DeliverySchedulePayload = {
  day_of_week?: DayOfWeekNumbers;
  frequency?: Frequency;
};

export function useUpdateDeliverySchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: DeliverySchedulePayload) => {
      const resp = await patchApiV2CustomersCustomerId({
        customer: payload,
      });

      if (resp.status !== StatusCode.SuccessOK) {
        throw new Error(JSON.stringify(resp.data, null, 2));
      }

      return resp.data;
    },
    onSuccess() {
      invalidateAccountSettings(queryClient);
      invalidateOrders(queryClient);
      invalidateOrdersV3(queryClient, { time_bucket: 'upcoming' });
      invalidateCustomerData(queryClient);
    },
  });
}
